import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Flex, Box } from 'grid-styled'
import classNames from 'classnames'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { getShares, getSharesSplit } from '../../api/otc/company/company'
import styles from './CompanyQuoteSecurity.module.scss'
import Outline from '../../components/Outline'
import Locale from '../../components/Locale'
import More from '../../components/More'
import TableFooter from '../../components/TableFooter'
import Table from '../../components/Table'
import Loading from '../../components/Loading'
import Select from '../../components/Select'

const filterValues = [{
    title: 'Shares Outstanding',
    value: 'tso'
}, {
    title: 'Authorized Shares',
    value: 'auth'
}, {
    title: 'Held at DTC',
    value: 'dtc'
}, {
    title: 'Unrestricted',
    value: 'unrestricted'
}, {
    title: 'Restricted',
    value: 'restricted'
}]

const buildShareLists = (rows) => {
    const lists = { tso: [], dtc: [], auth: [], unrestricted: [], restricted: [] }

    rows.forEach(row => {
        if (row.tsoShares != null) lists.tso.push({ row: row.row, secId: row.secId, sharesAdjusted: row.tsoSharesAdjusted, shares: row.tsoShares, date: row.tsoDate, source: row.tsoSource })
        if (row.dtcShares != null) lists.dtc.push({ row: row.row, secId: row.secId, sharesAdjusted: row.dtcSharesAdjusted, shares: row.dtcShares, date: row.dtcDate, source: row.dtcSource })
        if (row.authShares != null) lists.auth.push({ row: row.row, secId: row.secId, sharesAdjusted: row.authSharesAdjusted, shares: row.authShares, date: row.authDate, source: row.authSource })
        if (row.unrestrictedShares != null) lists.unrestricted.push({ row: row.row, secId: row.secId, sharesAdjusted: row.unrestrictedSharesAdjusted, shares: row.unrestrictedShares, date: row.unrestrictedDate, source: row.unrestrictedSource })
        if (row.restrictedShares != null) lists.restricted.push({ row: row.row, secId: row.secId, sharesAdjusted: row.restrictedSharesAdjusted, shares: row.restrictedShares, date: row.restrictedDate, source: row.restrictedSource })
    })

    return lists
}

const formulateGroupedData = (data) => {
    const result = [];
    let i = 0;

    while (i < data.length) {
        const current = data[i];
        let hasUnchangedHistory = false;
        let j = i + 1;

        // Find the next distinct row (different shares value)
        while (j < data.length && data[j].shares === current.shares) {
            hasUnchangedHistory = true;
            j++;
        }

        const nextDistinct = data[j];

        // === NEW LOGIC for determining which values to use ===
        let currentValue, nextValue;

        if (current.sharesAdjusted != null) {
            // Priority 1: Current has sharesAdjusted → use adjusted for both
            currentValue = current.sharesAdjusted;
            nextValue = nextDistinct?.sharesAdjusted != null
                ? nextDistinct.sharesAdjusted
                : nextDistinct?.shares;
        }
        else if (nextDistinct?.sharesAdjusted != null) {
            // Priority 2: Current has no sharesAdjusted, but next does
            currentValue = current.shares;
            nextValue = nextDistinct.sharesAdjusted;
        }
        else {
            // Priority 3: Neither has sharesAdjusted → original behavior
            currentValue = current.shares;
            nextValue = nextDistinct?.shares;
        }

        const change = nextValue != null ? currentValue - nextValue : null;
        const percentChange = nextValue != null && nextValue !== 0
            ? (change / nextValue) * 100
            : null;

        // Push the main (first) row
        result.push({
            ...current,
            ...(hasUnchangedHistory ? { hasUnchangedHistory: true } : {}),
            change,
            percentChange
        });

        // Push the unchanged history rows
        for (let k = i + 1; k < j; k++) {
            result.push({
                ...data[k],
                unchangedId: current.row,
                change: 0,
                percentChange: 0
            });
        }

        i = j;
    }

    return result;
}

const enrichShareLists = (lists, splits) => {
    return {
        tso: insertSplitRecords(formulateGroupedData(lists.tso), splits),
        dtc: insertSplitRecords(formulateGroupedData(lists.dtc), splits),
        auth: formulateGroupedData(lists.auth),
        unrestricted: insertSplitRecords(formulateGroupedData(lists.unrestricted), splits),
        restricted: insertSplitRecords(formulateGroupedData(lists.restricted), splits)
    }
}
const formatSplitRecord = (data) => {
    const split = { ...data }
    split.date = data.splitDate
    if (data.splitRatio) {
        const splitRatio = data.splitRatio.split(':')
        if (splitRatio[0] > splitRatio[1]) {
            split.splitType = 'Forward'
            split.sharesAdjusted = `${splitRatio[0]} For ${splitRatio[1]} Forward Split`
        } else {
            split.splitType = 'Reverse'
            split.sharesAdjusted = `${splitRatio[0]} For ${splitRatio[1]} Reverse Split`
        }
    }
    return split
}

const insertSplitRecords = (list, splits) => {
    if (!list || list.length === 0 || !splits || splits.length === 0) return list

    const combined = [
        ...list,
        ...splits.map(s => ({ ...s, isSplit: true }))
    ]

    combined.sort((a, b) => {
        const diff = new Date(b.date) - new Date(a.date)
        if (diff !== 0) return diff
        // Same date: split row comes first
        if (a.isSplit && !b.isSplit) return -1
        if (!a.isSplit && b.isSplit) return 1
        return 0
    })

    return combined
}

const INITIAL_SHARE_HISTORY = {
    loaded: false,
    data: { tso: [], dtc: [], auth: [], unrestricted: [], restricted: [] },
    error: null
}

const ShareStructureHistory = ({ securityId }) => {
    const [visibleCount, setVisibleCount] = useState(5)
    const [filterType, setFilterType] = useState(filterValues[0].value)
    const [shareHistory, setShareHistory] = useState(INITIAL_SHARE_HISTORY)
    const [expandedRows, setExpandedRows] = useState([])

    const loadShares = useCallback(async () => {
        const pageSize = 500

        try {
            const sharesPromise = (async () => {
                let page = 1, allData = []
                while (true) {
                    const data = await getShares({ page, pageSize, secId: securityId })
                    allData = allData.concat(data || [])
                    if (!data || data.length < pageSize) break
                    page++
                }
                return allData
            })()

            const [allData, splitsData] = await Promise.all([
                sharesPromise,
                getSharesSplit({ secId: securityId })
            ])

            const formattedSplits = (splitsData || []).map(formatSplitRecord)
            const lists = buildShareLists(allData)
            const enrichedLists = enrichShareLists(lists, formattedSplits)

            setShareHistory({ loaded: true, error: null, data: enrichedLists })
        } catch (err) {
            setShareHistory(prev => ({
                ...prev,
                loaded: false,
                error: 'Something went wrong, please try again.'
            }))
        }
    }, [securityId])

    useEffect(() => {
        loadShares()
    }, [loadShares])

    const onSelectfilterType = useCallback((type) => {
        setFilterType(type)
        setVisibleCount(5)
        setExpandedRows([])
    }, [])

    const onMore = useCallback(() => {
        setVisibleCount(prev => prev + 5)
    }, [])

    const handleRowExpand = useCallback((data) => {
        setExpandedRows(prev => {
            if (prev.includes(data.row)) {
                return prev.filter(row => row !== data.row)
            } else {
                return [...prev, data.row]
            }
        })
    }, [])

    const fullData = shareHistory.data[filterType] || []

    const visibleData = (() => {
        const result = []
        let parentCount = 0

        for (const item of fullData) {
            if (item.unchangedId) {
                // Only include if its parent is already in the result
                if (expandedRows.includes(item.unchangedId)) {
                    result.push(item)
                }
            } else {
                if (parentCount >= visibleCount) break
                result.push(item)
                parentCount++
            }
        }

        return result
    })()

    const hasAnySplits = fullData.some(item => item.isSplit === true)
    const totalParents = fullData.filter(item => !item.unchangedId).length
    const hasMore = visibleCount < totalParents

    const greyIfUnchanged = data => {
        if (data?.unchangedId) return '#939597'
        return 'inherit'
    }

    const hasSplit = data => {
        if (data?.isSplit) return true
        return false
    }

    const columns = [{
        name: 'date',
        header: 'As of Date',
        headerShort: 'Date',
        type: 'date',
        sortingEnabled: true,
        emptyValue: '-',
        color: greyIfUnchanged,
        bold: hasSplit
    }, {
        name: 'shares',
        header: 'Shares',
        type: 'int',
        sortingEnabled: true,
        emptyValue: hasSplit ? '' : '-',
        color: greyIfUnchanged
    }, ...(hasAnySplits ? [{
        name: 'sharesAdjusted',
        header: 'Shares Adjusted',
        headerShort: 'Shares Adj',
        align: 'right',
        component: data => hasSplit(data?.data)
            ? <b>{data.data?.sharesAdjusted}</b>
            : data.data?.sharesAdjusted
                ? <Locale type='int'>{data.data.sharesAdjusted}</Locale>
                : '',
        color: greyIfUnchanged
    }] : []), {
        name: 'change',
        type: 'int',
        header: 'Change',
        headerShort: 'Chg',
        emptyValue: hasSplit ? '' : '-',
        color: greyIfUnchanged
    }, {
        name: 'percentChange',
        type: 'sign',
        header: '% Change',
        headerShort: '% Chg',
        emptyValue: hasSplit ? '' : '-',
        color: greyIfUnchanged
    }, ...(fullData.find(item => item.unchangedId) ? [{
        name: 'hasUnchangedHistory',
        header: ' ',
        align: 'right',
        component: data => data?.data?.hasUnchangedHistory
            ? <button
                type='button'
                className={styles.expandChevron}
                onClick={() => handleRowExpand(data.data)}
                aria-expanded={expandedRows.includes(data.data.row)}
                aria-label={expandedRows.includes(data.data.row) ? 'Collapse row history' : 'Expand row history'}
            >
                {expandedRows.includes(data.data.row) ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            : undefined
    }]: [])
    ]

    return (
        <div className={classNames(styles.CompanyQuoteSecurity)}>
            <Outline mode='invisible' spacing='large' />
            <div className={styles.filterHeader}>
                <Flex>
                    <Box flex='1'><h4>Share Structure History</h4></Box>
                    <Box className={styles.tab}>
                        <Select
                            design='light'
                            options={filterValues}
                            defaultValue={filterType}
                            onSelect={onSelectfilterType}
                            required
                        />
                    </Box>
                </Flex>
            </div>
            <Loading
                type='table'
                height='10em'
                loaded={shareHistory.loaded}
                reloading={shareHistory.reloading}
                error={shareHistory.error}
                data={shareHistory.data[filterType]}
            >
                <Table columns={columns} data={visibleData} showHeader />
                <TableFooter>
                    <More onClick={onMore} disabled={!hasMore} />
                </TableFooter>
            </Loading>
        </div>
    )
}

ShareStructureHistory.propTypes = {
    securityId: PropTypes.number
}

export default ShareStructureHistory